import * as ChannelService from '@channel.io/channel-web-sdk-loader'
import { useContext, useEffect, useState } from 'react'
import { isAxiosError } from 'axios'
import dayjs from 'dayjs'
import styled from 'styled-components'

import { MODALS_ID } from '../../../constants'
import type { components } from '../../../constants/schema'

import { HttpReq, toast, clipboardCopy } from '../../../utils'
import { abrViewPromotionPage } from '../../../utils/abr'

import { StoresContext } from '../../../provider/StoreProvider'
import { ModalContext } from '../../../provider/modalProvider'

import { useNavigate } from '../../../hooks/useNavigate'
import useParamId from '../../../hooks/useParamId'

import { Wrap } from '../../../components/container/Wrap'
import { Content } from '../../../components/container/Content'
import { AlignCenterItem, RowBetween } from '../../../components/common'
import { DivideLine } from '../../../components/divide'
import { Header } from '../../../components/header'
import { Footer } from '../../../components/footer'
import { Button } from '../../../components/button'
import { Text } from '../../../components/text'
import { ArtipioContentsHeader, ArtipioContents, ArtipioContentItem } from '../../../components/contents'
import { TermsDetailModal } from '../../../components/modals/TermsDetailModal'

import { ReactComponent as HomeIcon } from '../../../assets/ico/detail/home_black.svg'
import { ReactComponent as ShareIcon } from '../../../assets/ico/detail/share_black.svg'
import { ReactComponent as CheckedIcon } from '../../../assets/ico/common/check/check_checked_blue_fill.svg'
import { ReactComponent as UncheckedIcon } from '../../../assets/ico/common/check/check_default_gray_fill.svg'

type Event = components['schemas']['EventCurrentDto']
type Term = components['schemas']['TermsDto']

export function EventDetail() {
  const { commonStore } = useContext(StoresContext)
  const { openModal, closeModal } = useContext(ModalContext)

  const [event, setEvent] = useState<Event | null>(null)
  const [selectTerms, setSelectTerms] = useState<Term[]>([])

  const navigate = useNavigate()
  const paramId = useParamId()

  const isTerms =
    !!event &&
    !!event.terms &&
    event.terms.length > 0 &&
    typeof event.overallStatus !== 'undefined' &&
    event.overallStatus !== 'UNKNOWN'

  useEffect(() => {
    commonStore.setShowTab(false)
  }, [])

  useEffect(() => {
    if (typeof paramId === 'undefined') {
      return
    }

    getEvent()
  }, [paramId])

  const getEvent = async () => {
    try {
      if (typeof paramId === 'undefined') {
        throw new Error('event id를 찾지 못했어요')
      }

      const response = await HttpReq<Event>({
        method: 'GET',
        url: `/event/${paramId}`,
      })

      abrViewPromotionPage(response ?? {})
      ChannelService.track('view_promotion_page')

      setEvent(response)
    } catch (error) {
      toast('페이지를 불러오는데 실패했습니다.', error)
    }
  }

  const participateEvent = async () => {
    if (event?.terms && event.terms.length !== selectTerms.length) {
      toast('모든 (선택) 약관에 동의해 주세요')
      return
    }

    try {
      await HttpReq<Event>({
        method: 'POST',
        url: `/event/${paramId}/approve`,
        data: {
          terms: selectTerms.map(({ id }) => ({ termsId: id, approved: true })),
        },
      })

      toast('이벤트 참여가 완료되었어요')

      getEvent()
    } catch (error) {
      if (isAxiosError(error)) {
        toast(error.response?.data.message)
      }
    }
  }

  const openTermsDetailModal = (termsId: number) => {
    const close = () => {
      closeModal(MODALS_ID.TERMS_DETAIL)
    }

    openModal({
      id: MODALS_ID.TERMS_DETAIL,
      Component: () => <TermsDetailModal termsId={termsId} onDismiss={close} />,
    })
  }

  const goHome = () => navigate('/home')

  const copyUrl = () =>
    clipboardCopy(window.location.href, { message: '이벤트 페이지 주소가 복사되었어요.', isFooter: paramId === 61 })

  return (
    <Wrap>
      <Header
        type="SUB"
        title=""
        rightItem={
          <HeaderRightItem $gap={16}>
            <ShareIcon onClick={copyUrl} />
            <HomeIcon onClick={goHome} />
          </HeaderRightItem>
        }
      />
      <StyledContent
        isLastImg={event?.contents?.[(event?.contents?.length as number) - 1]?.type === 'image'}
        isTerms={isTerms}
      >
        <ArtipioContentsHeader>
          <ArtipioContentsHeader.Title>{event?.title}</ArtipioContentsHeader.Title>
          <ArtipioContentsHeader.Date>
            {dayjs(new Date(event?.createdDate ?? '')).format('YYYY.MM.DD')}
          </ArtipioContentsHeader.Date>
        </ArtipioContentsHeader>
        <DivideLine isShort={true} space={24} />
        <ArtipioContents>
          {event?.contents?.map((content, i) => {
            const isFirstImg = content.type === 'image' && i === 0
            const isLastImg = content.type === 'image' && i === Number(event?.contents?.length) - 1
            return <ArtipioContentItem key={i} {...content} isFirstImg={isFirstImg} isLastImg={isLastImg} />
          })}
        </ArtipioContents>
        {isTerms && (
          <TermsList>
            {event.terms?.map((term) => {
              const { id, name, required } = term
              const isAgreed =
                (event.userTerms && event.userTerms?.findIndex((v) => v.id === id && v.approved) > -1) ||
                selectTerms.findIndex((v) => v.id === id) > -1

              const toggleTerm = () => {
                if (event.overallStatus !== 'CAN_APPROVE') {
                  return
                }

                setSelectTerms((prevState) =>
                  isAgreed ? prevState.filter((v) => v.id !== id) : [...prevState, { ...term }],
                )
              }

              const handleOpenTermsModal = () => {
                openTermsDetailModal(id)
              }

              return (
                <TermItem key={id}>
                  <AlignCenterItem $gap={8} onClick={toggleTerm}>
                    {isAgreed ? <CheckedIcon /> : <UncheckedIcon />}
                    <Text size={14}>{`(${required ? '필수' : '신택'}) ${name}`}</Text>
                  </AlignCenterItem>
                  <Text size={12} color="text_secondary" onClick={handleOpenTermsModal}>
                    보기
                  </Text>
                </TermItem>
              )
            })}
          </TermsList>
        )}
      </StyledContent>
      {process.env.NODE_ENV === 'production' && paramId === 61 && (
        <Footer>
          <Button type="primary" size="large" block={true} onClick={copyUrl}>
            이벤트 공유하기
          </Button>
        </Footer>
      )}
      {isTerms && (
        <Footer>
          <Button
            type="primary"
            size="large"
            disabled={event.overallStatus !== 'CAN_APPROVE'}
            block={true}
            onClick={participateEvent}
          >
            {event.overallStatusLabels}
          </Button>
        </Footer>
      )}
    </Wrap>
  )
}

const HeaderRightItem = styled(RowBetween)`
  > * {
    cursor: pointer;
  }
`

const StyledContent = styled(Content)<{ isLastImg: boolean; isTerms: boolean }>`
  ${({ isLastImg, isTerms }) => (!isLastImg || isTerms ? 'padding-bottom: 108px;' : '')}
`

const TermsList = styled.ul`
  display: flex;
  flex-direction: column;
  margin-top: 24px;
`

const TermItem = styled.li`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 24px;
  list-style: none;
  > span {
    text-decoration: underline;
  }
`
